import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PanelGroup, Panel } from "react-resizable-panels";

import ResizeHandle from "components/common/ResizeHandle";
import MaximizeHost from "components/common/MaximizeHost";
import MaximizablePanel from "components/common/MaximizablePanel";
import AssetWellsSelector from "../components/AssetWellsSelector";
import WellsSelector from "./WellsSelector";
import WellsMap from "../components/WellsMap";
import DeclineValidationChart from "./DeclineValidationChart";
import ForecastChart from "pages/dca/ForecastChart";
import DeclineHeroChart from "./DeclineHeroChart";
import AddProjectForm from "components/project/AddProjectForm";

import {
  reset,
  setActiveWell,
  setDcaWells,
  addDcaWell,
  setActiveDcaWell,
  selectDeclinePhase,
  selectDcaWells,
} from "features/decline_analysis/dcaProjectSlice";

import {
  setShowNewProjectPopup,
  addProjectItem,
  setActiveProjectId,
  selectActiveProject,
} from "features/project/projectsSlice";

import {
  getProjectDetail,
  saveDcaProjectDetail,
  parseProjectId,
} from "service/projects";
import ForecastParamsPanel from "./ForecastParamsPanel";
import DcaDiagnostics from "./components/DcaDiagnostics";
import useLoading from "components/common/useLoading";
import { Spin } from "antd";
import DcaManagementDataPanel from "./components/DcaManagementDataPanel";
import { PHASE_MODEL_MAP, DECLINE_PHASE_OPTIONS } from "app/codes";

function Dca(props) {
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = parseProjectId(searchParams.get("project_id"));
  // const projectType = searchParams.get("project_type");
  // const projectName = searchParams.get("project_name");
  const activeProject = useSelector(selectActiveProject);

  const declinePhase = useSelector(selectDeclinePhase);
  const product = declinePhase
    ? PHASE_MODEL_MAP[declinePhase]
    : PHASE_MODEL_MAP[DECLINE_PHASE_OPTIONS[0].value];

  // The 2nd well-selector (DCA-project-wells navigator) is only useful once the
  // project actually has analyzed wells; when empty it just wastes horizontal
  // space, so it collapses and the map / Available Data panel takes the width.
  const dcaWells = useSelector(selectDcaWells);
  const hasDcaWells = Array.isArray(dcaWells) && dcaWells.length > 0;

  const [projectLoadStatus, setProjectLoadStatus] = useState(null); // loading, loaded
  const [selectedWellIds, setSelectedWellIds] = useState(null);
  const [projectParamsToSave, setProjectParamsToSave] = useState(null);
  // DCA-C3: lower-row view toggle. "analysis" = existing validation+forecast
  // (untouched); "diagnostics" = flow-regime diagnostic view (new).
  const [lowerTab, setLowerTab] = useState("analysis");
  // UX-1: map demoted to collapsible context (no longer a golden-row 50%).
  const [mapOpen, setMapOpen] = useState(false);
  // UX-3: the original validation + forecast charts (which own Save / Validate /
  // phase controls) are retained but tucked into a collapsible "details" drawer
  // under the HERO chart, so no save path / diagnostic entry is lost.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [LoadingIcon, showLoadingIcon, hideLoadingIcon] = useLoading(
    <Spin size="large" />,
    50
  );

  const dispatch = useDispatch();

  const handleWellSelected = useCallback(
    (well) => {
      const wellId = well.uwi;
      // single select
      if (
        selectedWellIds == null ||
        selectedWellIds.includes(wellId) === false
      ) {
        setSelectedWellIds([wellId]);
        dispatch(setActiveDcaWell(wellId));
        dispatch(setActiveWell(well));
      }
    },
    [dispatch, selectedWellIds]
  );

  const handleDcaWellSelected = useCallback(
    (well) => {
      // single select
      if (
        selectedWellIds == null ||
        selectedWellIds.includes(well.uwi) === false
      ) {
        setSelectedWellIds([well.uwi]);
        dispatch(setActiveWell(well));
        dispatch(setActiveDcaWell(well.uwi));
      }
    },
    [dispatch, selectedWellIds]
  );

  const refreshData = useCallback(
    (projId) => {
      showLoadingIcon();
      setProjectLoadStatus("loading");
      getProjectDetail(
        projId,
        (result) => {
          setProjectLoadStatus("loaded");
          if (activeProject == null) {
            dispatch(
              addProjectItem({
                id: result.project_id,
                name: result.project_name,
                project_type: result.project_type,
                custom_data: result.custom_data,
              })
            );
            dispatch(setActiveProjectId(result.project_id));
          }
          const tempDcaWells = result["wells"];
          if (tempDcaWells) {
            dispatch(setDcaWells(tempDcaWells));
          }
          hideLoadingIcon();
        },
        (error) => {
          console.error(error);
          setProjectLoadStatus(null);
          hideLoadingIcon();
        }
      );
    },
    [dispatch, activeProject, showLoadingIcon, hideLoadingIcon]
  );

  useEffect(() => {
    return () => {
      dispatch(reset());
      dispatch(setActiveProjectId());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      projectLoadStatus === "loading" ||
      projectLoadStatus === "loaded" ||
      (projectId == null && activeProject == null)
    )
      return;
    refreshData(projectId || activeProject.id);
  }, [projectLoadStatus, projectId, activeProject, refreshData]);

  const submitDcaProject = (projId, projectParams) => {
    let data = Object.assign({}, projectParams);
    data.project_id = projId;
    showLoadingIcon();
    saveDcaProjectDetail(
      data,
      (res) => {
        setProjectParamsToSave(null);

        if (res?.success !== true) {
          hideLoadingIcon();
          console.error("DCA save failed!");
          return;
        }
        dispatch(addDcaWell(res.wells));
        hideLoadingIcon();
      },
      (error) => {
        console.error(error);
        hideLoadingIcon();
      }
    );
  };

  function handleSubmitDcaProject(projectParams) {
    if (projectId == null && activeProject == null) {
      dispatch(setShowNewProjectPopup(true));
      setProjectParamsToSave(projectParams);
      return;
    }

    submitDcaProject(projectId || activeProject.id, projectParams);
  }

  return (
    <div>
      <AddProjectForm
        input={{
          project_type: "dca",
          project_name: `PRJ_${new Date().getTime()}`,
          comment: "comment",
        }}
        callbackFunction={[submitDcaProject, projectParamsToSave]}
        loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
      />
      <LoadingIcon />
      <div
        className="page-content d-flex flex-column hide-scrollbar"
        style={{ position: "relative" }}
      >
        {/* Window "maximize / restore" host — overlays the split layout and
            covers ONLY this .page-content (left nav + breadcrumb stay visible). */}
        <MaximizeHost>
        {/* UX-REWORK · user-resizable split layout (react-resizable-panels).
            The outer vertical group splits the demoted CONTEXT STRIP from the
            HERO region; autoSaveId persists every divider drag to localStorage.
            The old fixed vh heights (22vh selectors / 30vh map / 18vh panel /
            52vh hero) are gone — the user sets the proportions by dragging. */}
        <PanelGroup
          direction="vertical"
          autoSaveId="dca-analyzer-v1"
          style={{ flexGrow: 1, minHeight: 0 }}
        >
          {/* CONTEXT STRIP — fixed-width selector(s) on the left; the map +
              Available Data panel fills the rest of the width. */}
          <Panel id="dca-context" order={1} defaultSize={26} minSize={12}>
            <div className="d-flex flex-row h-100">
              {/* Selectors: fixed width (each 142px via .well-selector). The
                  DCA-project-wells navigator (2nd column) renders ONLY when the
                  project has analyzed wells, so a fresh project reclaims that
                  space for the map / Available Data panel. */}
              <div
                className="d-flex flex-row h-100"
                style={{ flex: "0 0 auto" }}
              >
                <div
                  className="well-selector pe-1 h-100"
                  style={{ flexShrink: 0 }}
                >
                  <AssetWellsSelector
                    selectedWellIds={selectedWellIds}
                    onWellSelected={handleWellSelected}
                    loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                  />
                </div>
                {hasDcaWells && (
                  <div
                    className="well-selector pe-1 h-100"
                    style={{ flexShrink: 0 }}
                  >
                    <WellsSelector
                      selectedWellIds={selectedWellIds}
                      onWellSelected={handleDcaWellSelected}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  </div>
                )}
              </div>

              {/* Map + Available Data: fills remaining width. When the map is
                  open, the two stack in a vertical resizable group so the user
                  drags the map's HEIGHT (dca-context-map-v1 persists it); when
                  closed, Available Data fills the whole block. */}
              <div
                className="d-flex flex-column flex-grow-1 ps-1"
                style={{ minWidth: 0 }}
              >
                <div
                  className="d-flex flex-row mb-1"
                  style={{ flex: "0 0 auto" }}
                >
                  <span
                    className="btn-label header"
                    role="button"
                    onClick={() => setMapOpen((v) => !v)}
                    style={{ cursor: "pointer", marginRight: 8 }}
                    title="Show / hide the well-location map"
                  >
                    {mapOpen ? "▼ Map" : "▶ Map"}
                  </span>
                </div>
                {mapOpen ? (
                  <PanelGroup
                    direction="vertical"
                    autoSaveId="dca-context-map-v1"
                    style={{ flexGrow: 1, minHeight: 0 }}
                  >
                    <Panel id="dca-map" order={1} defaultSize={50} minSize={20}>
                      <MaximizablePanel
                        id="dca-map"
                        className="h-100 pb-1"
                        buttonStyle={{ right: 38 }}
                      >
                        <WellsMap
                          product={product}
                          selectedWellIds={selectedWellIds}
                          onWellSelected={handleWellSelected}
                          loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                        />
                      </MaximizablePanel>
                    </Panel>
                    <ResizeHandle direction="vertical" id="dca-map-hh" />
                    <Panel id="dca-avail" order={2} defaultSize={50} minSize={20}>
                      <MaximizablePanel
                        id="dca-avail"
                        className="h-100 pt-1"
                        buttonStyle={{ right: 44 }}
                      >
                        <DcaManagementDataPanel
                          selectedWellIds={selectedWellIds}
                          loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                        />
                      </MaximizablePanel>
                    </Panel>
                  </PanelGroup>
                ) : (
                  <MaximizablePanel
                    id="dca-avail"
                    className="flex-grow-1"
                    buttonStyle={{ right: 44 }}
                  >
                    <DcaManagementDataPanel
                      selectedWellIds={selectedWellIds}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  </MaximizablePanel>
                )}
              </div>
            </div>
          </Panel>

          <ResizeHandle direction="vertical" id="dca-v-handle" />

          {/* HERO REGION — tab strip (fixed) + resizable hero/params split. */}
          <Panel id="dca-lower" order={2} defaultSize={74} minSize={30}>
            <div className="d-flex flex-column h-100">
              {/* Tab strip (DCA-C3): Analysis / Flow-Regime Diagnostics +
                  reserves report entry — unchanged behaviour. */}
              <div
                className="d-flex flex-row mb-1"
                style={{ flex: "0 0 auto" }}
              >
                <span
                  className={
                    "btn-label header" +
                    (lowerTab === "analysis" ? " active" : "")
                  }
                  role="button"
                  onClick={() => setLowerTab("analysis")}
                  style={{
                    cursor: "pointer",
                    opacity: lowerTab === "analysis" ? 1 : 0.55,
                    marginRight: 8,
                  }}
                >
                  Analysis
                </span>
                <span
                  className={
                    "btn-label header" +
                    (lowerTab === "diagnostics" ? " active" : "")
                  }
                  role="button"
                  onClick={() => setLowerTab("diagnostics")}
                  style={{
                    cursor: "pointer",
                    opacity: lowerTab === "diagnostics" ? 1 : 0.55,
                  }}
                >
                  Flow-Regime Diagnostics
                </span>
                {/* WS6 RS-B: entry point to the reserves report. Carries the
                    current project_id so the reporting page can preselect it. */}
                <span
                  className="btn-label header"
                  role="button"
                  onClick={() => {
                    const pid = projectId || activeProject?.id;
                    navigate(
                      pid
                        ? `/decline_analysis/reporting?project_id=${pid}`
                        : "/decline_analysis/reporting"
                    );
                  }}
                  style={{ cursor: "pointer", marginLeft: "auto" }}
                  title="Generate a PRMS/SEC reserves report for this project"
                >
                  Generate Reserves Report
                </span>
              </div>

              {lowerTab === "diagnostics" ? (
                <MaximizablePanel id="dca-diag" className="flex-grow-1">
                  <DcaDiagnostics
                    loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                  />
                </MaximizablePanel>
              ) : (
                <>
                  {/* HERO chart | (draggable) | permanent forecast-param panel. */}
                  <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
                    <PanelGroup
                      direction="horizontal"
                      autoSaveId="dca-analysis-h1"
                      style={{ flexGrow: 1, minHeight: 0 }}
                    >
                      <Panel id="dca-hero" order={1} defaultSize={74} minSize={40}>
                        <MaximizablePanel
                          id="dca-hero"
                          className="h-100 pe-1"
                          buttonStyle={{ right: 72 }}
                        >
                          <DeclineHeroChart
                            loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                          />
                        </MaximizablePanel>
                      </Panel>

                      <ResizeHandle direction="horizontal" id="dca-analysis-hh" />

                      <Panel
                        id="dca-params"
                        order={2}
                        defaultSize={26}
                        minSize={16}
                        maxSize={45}
                      >
                        <MaximizablePanel id="dca-params" className="h-100">
                          <ForecastParamsPanel />
                        </MaximizablePanel>
                      </Panel>
                    </PanelGroup>
                  </div>

                  {/* UX-3 · original validation + forecast charts (own Save /
                      Validate) retained in a collapsible drawer — no save path
                      lost. Kept out of the PanelGroup as an auto-height drawer. */}
                  <div className="mt-2" style={{ flex: "0 0 auto" }}>
                    <span
                      className="btn-label header"
                      role="button"
                      onClick={() => setDetailsOpen((v) => !v)}
                      style={{ cursor: "pointer" }}
                      title="Show the model-validation & forecast detail charts (Save / Validate here)"
                    >
                      {detailsOpen
                        ? "▼ Validation & Forecast details (Save / Validate)"
                        : "▶ Validation & Forecast details (Save / Validate)"}
                    </span>
                    {detailsOpen && (
                      <div
                        className="row g-0 mt-1"
                        style={{ height: "40vh", minHeight: 0 }}
                      >
                        <div className="col w-50 ps-1 pe-1 h-100">
                          <DeclineValidationChart
                            submitDcaProject={handleSubmitDcaProject}
                            loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                          />
                        </div>
                        <div className="col w-50 ps-1 h-100">
                          <ForecastChart
                            submitDcaProject={handleSubmitDcaProject}
                            loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Panel>
        </PanelGroup>
        </MaximizeHost>
      </div>
    </div>
  );
}

export default Dca;
